import { useEffect } from 'react'

export interface ShortcutDef {
  /** Key to match (e.g. 'y', 'Escape', ' '). Matches e.key by default, or e.code if useCode is true */
  key: string
  ctrl?: boolean
  alt?: boolean
  shift?: boolean
  meta?: boolean
  /** Match against e.code instead of e.key (e.g. 'KeyP') */
  useCode?: boolean
  handler: () => void
}

/**
 * Register keyboard shortcuts with automatic cleanup, input guard, and modifier matching.
 *
 * @param shortcuts - Array of shortcut definitions
 * @param enabled - Whether shortcuts are active (e.g. dev mode unlocked)
 */
export function useKeyboardShortcuts(shortcuts: ShortcutDef[], enabled = true) {
  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip when typing in form elements
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return
      }

      for (const s of shortcuts) {
        const keyMatch = s.useCode ? e.code === s.key : e.key === s.key
        if (!keyMatch) continue
        if (!!s.ctrl !== e.ctrlKey) continue
        if (!!s.alt !== e.altKey) continue
        if (!!s.shift !== e.shiftKey) continue
        if (!!s.meta !== e.metaKey) continue

        e.preventDefault()
        s.handler()
        return
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [shortcuts, enabled])
}
